import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Users, Package, BarChart2, RefreshCw, AlertTriangle,
  ChevronDown, ChevronRight, UserPlus, UserMinus, CheckCircle2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

const PACKAGE_COLORS = {
  basic: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  silver: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  gold: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300',
  platinum: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
};

const PACKAGES = ['basic', 'silver', 'gold', 'platinum'];

function UsageMiniBar({ used, limit }) {
  if (limit === 0) return <span className="text-xs text-red-500">—</span>;
  const pct = Math.min(100, Math.round((used / limit) * 100));
  const color = pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-yellow-500' : 'bg-green-500';
  return (
    <div className="flex items-center gap-1.5">
      <div className="w-20 bg-slate-200 dark:bg-slate-700 rounded-full h-1.5">
        <div className={`h-1.5 rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-slate-500">{used}/{limit}</span>
    </div>
  );
}

function ClientCard({ client, onPackageChange, onRemove }) {
  const [expanded, setExpanded] = useState(false);
  const [assigning, setAssigning] = useState(false);

  const handlePackageChange = async (pkg) => {
    setAssigning(true);
    try {
      await onPackageChange(client.client_id, pkg);
    } finally {
      setAssigning(false);
    }
  };

  const keyMetrics = ['keywords', 'blog_posts', 'backlinks', 'gbp_posts', 'ai_recommendations'];

  return (
    <Card className="border border-slate-200 dark:border-slate-700">
      <CardContent className="p-4 space-y-3">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="font-medium text-sm text-slate-900 dark:text-slate-100">
              {client.client_name || client.client_id}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">{client.client_email}</div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-slate-400 hover:text-red-500"
              title="Unlink client"
              onClick={() => onRemove(client.client_id)}
            >
              <UserMinus className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Projects summary */}
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <span>{client.projects.length} project{client.projects.length !== 1 ? 's' : ''}</span>
        </div>

        {/* Per-project usage */}
        {client.projects.length > 0 && (
          <div>
            <button
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 mb-2"
              onClick={() => setExpanded(e => !e)}
            >
              {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
              {expanded ? 'Hide' : 'Show'} usage breakdown
            </button>

            {expanded && (
              <div className="space-y-3 pl-2 border-l border-slate-200 dark:border-slate-700">
                {client.projects.map(proj => (
                  <div key={proj.project_id} className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-slate-700 dark:text-slate-300">
                        {proj.project_name || proj.project_id}
                      </span>
                      {proj.package_type && (
                        <Badge className={`text-[10px] px-1.5 py-0 ${PACKAGE_COLORS[proj.package_type] || ''}`}>
                          {proj.package_type}
                        </Badge>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
                      {keyMetrics.map(m => {
                        const used = proj.counters?.[m] ?? 0;
                        const limit = proj.limits?.[m] ?? 0;
                        if (limit === undefined) return null;
                        return (
                          <div key={m} className="flex items-center justify-between gap-2">
                            <span className="text-[10px] text-slate-500 capitalize w-20 truncate">{m.replace(/_/g, ' ')}</span>
                            <UsageMiniBar used={used} limit={limit} />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Package assignment */}
        <div className="pt-1 border-t border-slate-100 dark:border-slate-800 flex items-center gap-2">
          <Package className="h-3.5 w-3.5 text-slate-400" />
          <span className="text-xs text-slate-500">Assign package:</span>
          <Select
            disabled={assigning}
            value={client.projects[0]?.package_type || ''}
            onValueChange={handlePackageChange}
          >
            <SelectTrigger className="h-6 text-xs w-28 py-0">
              <SelectValue placeholder="Select…" />
            </SelectTrigger>
            <SelectContent>
              {PACKAGES.map(p => (
                <SelectItem key={p} value={p} className="text-xs capitalize">{p}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {assigning && <RefreshCw className="h-3 w-3 animate-spin text-slate-400" />}
        </div>
      </CardContent>
    </Card>
  );
}

export default function AgencyDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addClientId, setAddClientId] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/agency/usage');
      setData(res.data);
    } catch (err) {
      toast.error('Failed to load agency usage');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleAddClient = async () => {
    if (!addClientId.trim()) return;
    setAdding(true);
    try {
      await api.post(`/agency/clients/${addClientId.trim()}`);
      toast.success('Client linked successfully');
      setAddClientId('');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to link client');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveClient = async (clientId) => {
    if (!window.confirm('Remove this client from your agency?')) return;
    try {
      await api.delete(`/agency/clients/${clientId}`);
      toast.success('Client unlinked');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to unlink client');
    }
  };

  const handlePackageChange = async (clientId, pkg) => {
    try {
      await api.patch(`/agency/clients/${clientId}/package`, { package_type: pkg });
      toast.success(`Package updated to ${pkg}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update package');
    }
  };

  // Stats
  const totalProjects = data?.clients?.reduce((s, c) => s + c.projects.length, 0) ?? 0;
  const totalClients = data?.clients?.length ?? 0;
  const pkgBreakdown = {};
  data?.clients?.forEach(c => {
    c.projects.forEach(p => {
      if (p.package_type) pkgBreakdown[p.package_type] = (pkgBreakdown[p.package_type] || 0) + 1;
    });
  });

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Agency Dashboard</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            Manage your clients and monitor their package usage
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Clients', value: totalClients, icon: Users },
          { label: 'Projects', value: totalProjects, icon: BarChart2 },
          ...PACKAGES.map(p => ({
            label: p.charAt(0).toUpperCase() + p.slice(1),
            value: pkgBreakdown[p] ?? 0,
            icon: Package,
          })),
        ].slice(0, 4).map(stat => (
          <Card key={stat.label} className="border border-slate-200 dark:border-slate-700">
            <CardContent className="p-4 flex items-center gap-3">
              <stat.icon className="h-5 w-5 text-indigo-500 flex-shrink-0" />
              <div>
                <div className="text-xl font-bold text-slate-900 dark:text-slate-100">{stat.value}</div>
                <div className="text-xs text-slate-500">{stat.label}</div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Add client */}
      <Card className="border border-slate-200 dark:border-slate-700">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <UserPlus className="h-4 w-4" /> Link a New Client
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          <div className="flex gap-2">
            <input
              type="text"
              className="flex-1 h-9 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter client user_id…"
              value={addClientId}
              onChange={e => setAddClientId(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleAddClient()}
            />
            <Button size="sm" onClick={handleAddClient} disabled={adding || !addClientId.trim()}>
              {adding ? <RefreshCw className="h-4 w-4 animate-spin" /> : 'Add'}
            </Button>
          </div>
          <p className="text-xs text-slate-400 mt-1.5">The client must already have an account in the system.</p>
        </CardContent>
      </Card>

      {/* Client list */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-36 rounded-xl bg-slate-100 dark:bg-slate-800 animate-pulse" />
          ))}
        </div>
      ) : data?.clients?.length === 0 ? (
        <Card className="border border-dashed border-slate-300 dark:border-slate-700">
          <CardContent className="py-12 flex flex-col items-center text-center gap-3">
            <Users className="h-10 w-10 text-slate-300" />
            <div className="text-slate-500 text-sm">No clients linked yet.</div>
            <div className="text-slate-400 text-xs">Use the form above to add your first client.</div>
          </CardContent>
        </Card>
      ) : (
        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 gap-4"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
        >
          {data.clients.map(client => (
            <motion.div
              key={client.client_id}
              variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
            >
              <ClientCard
                client={client}
                onPackageChange={handlePackageChange}
                onRemove={handleRemoveClient}
              />
            </motion.div>
          ))}
        </motion.div>
      )}
    </div>
  );
}
